import { Component, EnvironmentInjector, inject, NgZone, OnDestroy } from '@angular/core';
import { AlertController, IonicModule, ModalController, NavController, Platform } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { TranslateModule } from './shared/translate/translate.module';
import { filter, firstValueFrom, map, Subscription } from 'rxjs';
import { LoggerService } from './shared/services/logger.service';
import { TranslatePipe } from './shared/translate/pipes/translate.pipe';
import { AuthService } from './shared/services/auth.service';
import { Deeplinks } from '@awesome-cordova-plugins/deeplinks/ngx';
import { VersionService } from './shared/services/version.service';
import { register } from 'swiper/element/bundle';
import { TranslateService } from './shared/translate/services/translate.service';
import { FcmService } from './shared/services/fcm.service';
import { SoundService } from './shared/services/sound.service';
import { BrowserTab } from '@awesome-cordova-plugins/browser-tab/ngx';
import { NavigationError, Router } from '@angular/router';
import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx';
import { SplashTextComponent } from './shared/components/splash-text/splash-text.component';
import { AnalyticsService } from './shared/services/analytics.service';

declare const universalLinks: any;
register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, TranslateModule, SplashTextComponent]
})
export class AppComponent implements OnDestroy {
  showSplash = true;
  splashStarted = false;
  private startupSoundEvaluated = false;
  private startLogged = false;
  user: firebase.default.User | null | undefined;
  isLandscape: boolean = false;
  orientationAlert: HTMLIonAlertElement | null = null;
  navigationErrorSub?: Subscription;
  deepLinksSub?: Subscription;
  universalLinksSub?: any;

  public environmentInjector = inject(EnvironmentInjector);
  constructor(
    private platform: Platform,
    private translate: TranslatePipe,
    private logger: LoggerService,
    private authService: AuthService,
    private navController: NavController,
    private zone: NgZone,
    private modalController: ModalController,
    private alertController: AlertController,
    private deeplinks: Deeplinks,
    private versionService: VersionService,
    private translateService: TranslateService,
    private fcmService: FcmService,
    private soundService: SoundService,
    private browserTab: BrowserTab,
    private router: Router,
    private splashScreen: SplashScreen,
    private analytics: AnalyticsService
  ) {
    this.initializeApp();
  }

  async initializeApp() {
    this.navigationErrorSub = this.router.events.pipe(
      filter(e => e instanceof NavigationError),
      map(e => e as NavigationError)
    ).subscribe(e => {
      if (e.error instanceof Error && e.error.name == 'ChunkLoadError' && navigator.onLine) {
        navigator.onLine
        console.log("CHUNK LOAD RECOVER")
        window.location.assign(e.url);
      }
    });

    this.platform.ready().then(async () => {
      if (this.isRunningOnMobileDevice()) {
        try { this.splashScreen.hide(); } catch { /* Native splash is optional on unsupported shells. */ }
      }
      this.splashStarted = true;
      if (!this.startLogged) {
        this.startLogged = true;
        this.analytics.log('student_app_started', { platform: this.platform.is('android') ? 'android' : this.platform.is('ios') ? 'ios' : 'web' });
      }
      if (!this.startupSoundEvaluated) {
        this.startupSoundEvaluated = true;
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.soundService.playUnlock();
        }
      }
      this.translateService.use('hu');
      if (this.isMobileWebApp()) {
        this.checkOrientation();
        window.addEventListener('resize', () => this.checkOrientation());
      }
      if (this.isRunningOnMobileDevice()) {
        await this.initializeBadge();
        this.versionService.subscribeToVersionChanges();
      }
      try {
        this.tryAutoLogin();
        const user = await firstValueFrom(this.authService.currentUserObservable());
        if (user) {
          this.logger.setEmail(user.email);
          this.logger.setUserId(user.uid);
          this.fcmService.checkNotifications(user.email || '');
        }
      } catch (error) {
        this.logger.handleError(error);
      }
      if (this.isRunningOnMobileDevice()) {
        this.initUniversalLinks();
        await this.handleBackButton();
      }
    });
  }

  finishSplash(): void {
    if (this.showSplash) {
      this.analytics.log('student_splash_played', { reduced_motion: !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches });
    }
    this.showSplash = false;
  }

  isRunningOnMobileDevice(): boolean {
    return this.platform.is('cordova');
  }

  async handleBackButton() {
    this.platform?.backButton?.subscribeWithPriority(10, async () => {
      if (this.orientationAlert && this.isLandscape) {
        return;
      }
      const alert = await this.alertController?.getTop();
      const modal = await this.modalController?.getTop();
      if (modal || alert) {
        modal ? await modal?.dismiss() : null;
        alert ? await alert?.dismiss() : null;
      }
    });
  }

  ngOnDestroy() {
    this.navigationErrorSub?.unsubscribe();
    this.deepLinksSub?.unsubscribe();
    this.universalLinksSub?.unsubscribe();
  }

  initUniversalLinks() {
    this.deepLinksSub = this.deeplinks
      .route({
        'x': 'x',
      })
      .subscribe(
        match => {
          const path = String(match.$link.path) || null;
          const host = String(match.$link.host) || null;
          const queryString = String(match.$link.queryString) || '';
          const queryParams: any = {};

          if (queryString) {
            queryString.split('&').forEach(param => {
              const [key, value] = param.split('=');
              queryParams[key] = value;
            });
          }

          let url = '';
          if (match.$link.scheme === 'recogapp') {
            if (path === '/' || !path) {
              url = host || '';
            } else {
              url = path;
            }
          } else {
            url = path || host || '';
          }

          if (url && !url.startsWith('/')) {
            url = '/' + url;
          }
          this.logger.addLogItem('DEEPLINK_ROUTE', { match, url, queryParams });

          if (url) {
            this.closeBrowserTab();
            this.zone.run(() => {
              console.log("navigating to url: ", url, "with params: ", queryParams)
              this.navController.navigateRoot(url, { queryParams });
            });
          }
        },
        _nomatch => { }
      );
    this.universalLinksSub = universalLinks.subscribe(
      'ul_appOpened',
      (link: any) => {
        const url = String(link.path) || null;
        this.logger.addLogItem('UNIVERSAL_LINKS', { link, url });
        if (url) {
          this.zone.run(() => {
            this.navController.navigateRoot(url);
          });
        }
      },
      (error: any) => {
        this.logger.handleError(error, true);
      }
    );
  }

  closeBrowserTab() {
    this.browserTab.isAvailable().then(isAvailable => {
      if (isAvailable) {
        this.browserTab.close();
      }
    });
  }

  ionViewDidLeave() {
    this.platform?.backButton?.unsubscribe();
  }

  isMobileWebApp(): boolean {
    return !this.platform.is('cordova') && (this.platform.is('android') || this.platform.is('iphone'));
  }

  async checkOrientation() {
    const isLandscapeNow = this.platform.isLandscape();

    if (isLandscapeNow !== this.isLandscape) {
      this.isLandscape = isLandscapeNow;

      if (isLandscapeNow) {
        this.showOrientationWarning();
      } else if (this.orientationAlert) {
        this.orientationAlert.dismiss();
        this.orientationAlert = null;
      }
    }
  }

  async showOrientationWarning() {
    if (this.orientationAlert) {
      return;
    }

    const message = this.translate.transform('LANDSCAPE_WARNING');

    this.orientationAlert = await this.alertController.create({
      message: message,
      backdropDismiss: false,
      keyboardClose: false,
      cssClass: 'landscape-alert'
    });

    await this.orientationAlert.present();

    if (this.orientationAlert) {
      this.orientationAlert.onDidDismiss().then(() => {
        if (this.isLandscape) {
          this.showOrientationWarning();
        }
      });
    }
  }


  private tryAutoLogin() {
    if (location.hostname === 'localhost') {
      const cookies = document.cookie.split(';').reduce((acc: any, curr) => {
        const [name, val] = curr.trim().split('=');
        acc[name] = decodeURIComponent(val);
        return acc;
      }, {});

      if (cookies['autoLogin']) {
        try {
          const loginData = JSON.parse(cookies['autoLogin']);
          if (loginData && loginData.email && loginData.password) {
            this.authService.login(loginData.email, loginData.password).then(() => {
              this.navController.navigateRoot(loginData.page);
            })
          }
        } catch (e) {
          console.error('Cookie error', e);
        }
      }
    }
  }

  async initializeBadge() {
    try {
      //await this.badge.set(5);
    } catch (error) {
      this.logger.handleError(error);
    }
  }
}
